/**
 * La línea de tendencia de una tarjeta. Sin ejes, sin rótulos, sin color.
 *
 * `StatTile` declaraba «sin gráfica: aquí la respuesta correcta es el número».
 * La mitad de ese argumento se mantiene y la otra se revierte a propósito. Se
 * mantiene que la VARIACIÓN no se colorea: en un balance no existe una
 * dirección buena —más pasivo no es peor, depende de por qué—, y pintarla de
 * verde o rojo afirmaría un juicio que el dato no sostiene. Se revierte que no
 * haya dibujo: una línea en tinta neutra no emite juicio, dice por dónde ha
 * venido la cifra, y esa es la pregunta que un saldo suelto deja sin responder.
 *
 * Por eso va en `--ink-muted` y no en un color de serie: aquí no identifica una
 * entidad frente a otras, acompaña a un número que ya está escrito al lado.
 */

import { type Cifra, cifras, escala, ruta } from "./escala";

interface Props {
  /** Crudo de la API: puede venir como cadena. Se convierte al entrar. */
  valores: Cifra[];
  /** Qué serie es, para el lector de pantalla. */
  etiqueta: string;
  ancho?: number;
  alto?: number;
}

export function Sparkline({ valores, etiqueta, ancho = 104, alto = 26 }: Props) {
  const puntos = cifras(valores);
  const limpios = puntos.filter((v): v is number => v !== null);
  // Con menos de dos puntos no hay tendencia que dibujar, y una línea de un
  // solo punto es una raya horizontal que sugiere estabilidad inventada.
  if (limpios.length < 2) return null;

  // El recorrido REAL de la serie, no `extremos`: ese mete el cero a la fuerza
  // para que una barra no exagere una diferencia, y aquí no hay ni eje ni línea
  // base que exagerar. Con saldos de mil millones, forzar el cero deja la
  // variación de un mes en un píxel dentro de una caja de 26: la línea sale
  // plana y dice lo contrario de lo que pasó.
  const min = Math.min(...limpios);
  const max = Math.max(...limpios);
  const y = escala(min === max ? [min, min + 1] : [min, max], [alto - 2.5, 2.5]);
  const x = (i: number) => (i / (puntos.length - 1)) * (ancho - 3) + 1.5;
  const ultimo = puntos.length - 1;
  const cierre = puntos[ultimo];

  return (
    <svg
      className="sparkline"
      width={ancho}
      height={alto}
      viewBox={`0 0 ${ancho} ${alto}`}
      role="img"
      aria-label={`Evolución de ${etiqueta} en el período`}
    >
      <path d={ruta(puntos, x, y)} fill="none" strokeWidth="1.5" />
      {cierre !== null && (
        /* El punto final ancla la lectura: sin él, la línea parece continuar
           fuera del recuadro y no se ve dónde está HOY la cifra. */
        <circle cx={x(ultimo)} cy={y(cierre)} r="2" />
      )}
    </svg>
  );
}
